"use client";
/*
 * Documentation:
 * DC_Media_Link_Card — https://app.subframe.com/4f62853ee923/library?component=DC_Media_Link_Card_fa9d6a5e-356f-4635-b7d5-b1388c878834
 */

import React from "react";
import { FeatherExternalLink } from "@subframe/core";
import { FeatherGlobe } from "@subframe/core";
import { FeatherVideo } from "@subframe/core";
import * as SubframeCore from "@subframe/core";
import * as SubframeUtils from "../utils";

interface Dc_Media_Link_CardRootProps
  extends React.HTMLAttributes<HTMLDivElement> {
  isContent?: boolean;
  hasNoImage?: boolean;
  isSourceVideo?: boolean;
  text?: React.ReactNode;
  publisher?: React.ReactNode;
  imageSlot?: React.ReactNode;
  noContainer?: boolean;
  icon?: React.ReactNode;
  className?: string;
}

const Dc_Media_Link_CardRoot = React.forwardRef<
  HTMLDivElement,
  Dc_Media_Link_CardRootProps
>(function Dc_Media_Link_CardRoot(
  {
    isContent = false,
    hasNoImage = false,
    isSourceVideo = false,
    text,
    publisher,
    imageSlot,
    noContainer = false,
    icon = <FeatherGlobe />,
    className,
    ...otherProps
  }: Dc_Media_Link_CardRootProps,
  ref
) {
  return (
    <div
      className={SubframeUtils.twClassNames(
        "group/fa9d6a5e flex w-full cursor-pointer flex-col items-start gap-4 rounded-md bg-neutral-50 px-4 py-4 hover:bg-neutral-100",
        {
          "bg-transparent px-0 py-0 hover:bg-transparent": noContainer,
          "flex-row flex-nowrap gap-4": isContent,
        },
        className
      )}
      ref={ref}
      {...otherProps}
    >
      {imageSlot ? (
        <div
          className={SubframeUtils.twClassNames(
            "flex w-full flex-col items-start gap-4 overflow-hidden rounded-md",
            { hidden: isSourceVideo || hasNoImage, "basis-1/2": isContent }
          )}
        >
          {imageSlot}
        </div>
      ) : null}
      <div
        className={SubframeUtils.twClassNames(
          "flex w-full flex-col items-start gap-4",
          {
            "px-0 py-0": noContainer,
            "h-auto grow shrink-0 self-stretch basis-1/2": isContent,
          }
        )}
      >
        {icon ? (
          <SubframeCore.IconWrapper
            className={SubframeUtils.twClassNames(
              "text-body font-body text-default-font",
              { hidden: isSourceVideo }
            )}
          >
            {icon}
          </SubframeCore.IconWrapper>
        ) : null}
        <FeatherVideo
          className={SubframeUtils.twClassNames(
            "hidden text-body font-body text-default-font",
            { "inline-flex": isSourceVideo }
          )}
        />
        <div
          className={SubframeUtils.twClassNames(
            "flex w-full flex-col items-start gap-4",
            {
              "w-full grow shrink-0 basis-0 items-start justify-end": isContent,
            }
          )}
        >
          {text ? (
            <span
              className={SubframeUtils.twClassNames(
                "w-full text-caption-bold font-caption-bold text-default-font",
                {
                  "group-hover/fa9d6a5e:underline group-hover/fa9d6a5e:underline-offset-2":
                    noContainer,
                }
              )}
            >
              {text}
            </span>
          ) : null}
          <div className="flex w-full items-center gap-2">
            <FeatherExternalLink className="text-caption font-caption text-subtext-color" />
            {publisher ? (
              <span className="text-caption font-caption text-subtext-color">
                {publisher}
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
});

export const Dc_Media_Link_Card = Dc_Media_Link_CardRoot;
